/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onMount,
} from "solid-js";

import GradientBorderButton from "@/components/Base/GradientBorderButton";
// import { ExpandAnimationWrapper } from "@/components/Base/AnimationWrapper";
import { Input } from "@/components/Base/Input/Input.tsx";
import Popup from "@/components/Base/Popup/Popup.tsx";
import Icon, { svgPathTypes } from "@/components/Icon.tsx";
import { useOnClickOutside } from "@/hooks/useClickOutside";
import { useI18n } from "@/i18n";
import { isMobile } from "@/lib/isMobile";
import { getProviderLogo } from "@/lib/resFolder";
import { cn } from "@/lib/tailwindcssTool";

interface Option {
  label: string;
  value: string | number;
  [key: string]: any;
}

interface SelectProps {
  options: Option[];
  label?: string;
  placeholder?: string;
  defaultValue?: string | number | (string | number)[];
  multiple?: boolean;
  class?: string;
  inputClass?: string;
  downClass?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  bgColor?:
    | "bg-general-bg2"
    | "bg-home-bg2"
    | "bg-general-enterbg"
    | "bg-general-bg3";
  isClickOutsideClose?: boolean;
  onChange?: (value: string | number | (string | number)[]) => void;
  showPrefix?: boolean;
  prefixText?: string;
  showSelectedPrefix?: boolean;
  selectedPrefixText?: string | JSX.Element;
  arrowIcon?: "general/arrow_down" | "under_arrow";
}

function IconCheckbox(props: { checked?: boolean }) {
  return (
    <div
      class="border-(--colors-icon-checkbox-border) flex size-5 shrink-0 items-end justify-center rounded-[6px] border"
      classList={{
        "size-6": !isMobile(),
        "bg-(--colors-icon-checkbox-bg-active)": props.checked,
      }}
    >
      <Show when={props.checked}>
        <Icon name="general/check" color="var(--colors-icon-invert-btn)" />
      </Show>
    </div>
  );
}

function IconRadio(props: { checked?: boolean }) {
  return (
    <div
      class={
        "border-(--colors-icon-checkbox-border) flex size-5 items-center justify-center rounded-full border"
      }
      classList={{
        "size-6": !isMobile(),
      }}
    >
      {props.checked && (
        <div
          class={"bg-(--colors-icon-checkbox-bg-active) size-2.5 rounded-full"}
          classList={{
            "!size-3": !isMobile(),
          }}
        />
      )}
    </div>
  );
}

export default function Select(props: SelectProps) {
  const { t } = useI18n();
  const isMultiple = props.multiple ?? false;
  const [open, setOpen] = createSignal(false);
  const [searchQuery, setSearchQuery] = createSignal("");
  const [filteredOptions, setFilteredOptions] = createSignal<Option[]>(
    props.options,
  );
  const [selected, setSelected] = createSignal<Option[]>(
    Array.isArray(props.defaultValue)
      ? props.options.filter((opt) =>
          (props.defaultValue as (string | number)[]).includes(opt.value),
        )
      : props.defaultValue !== undefined
        ? props.options.filter((opt) => opt.value === props.defaultValue) // 如果是单个值
        : [],
  );
  const [popupRef, setPopupRef] = createSignal<{ handleClose: () => void }>();

  const Prefix = (): JSX.Element => {
    return (
      <Icon
        name="casino/search"
        class="size-4 text-[var(--colors-icon-tab-default)]"
      />
    );
  };

  createMemo(() => {
    if (props.options) {
      if (isMultiple) {
        setSelected([]);
      } else if (props.defaultValue !== undefined) {
        // 当 options 或 defaultValue 改变时，重新设置选中值
        const newSelected = props.options.filter(
          (opt) => opt.value === props.defaultValue,
        );
        setSelected(newSelected);
      }
    }
  });

  // 添加对 defaultValue 的监听
  createEffect(
    on(
      createMemo(() => props.defaultValue !== undefined && !isMultiple),
      (boolean) => {
        if (boolean) {
          const newSelected = props.options.filter(
            (opt) => opt.value === props.defaultValue,
          );
          setSelected(newSelected);
        }
      },
    ),
  );

  const toggle = () => setOpen((prev) => !prev);

  const selectItem = (item: Option) => {
    if (isMultiple) {
      const current = selected();
      const exists = current.find((i) => i.value === item.value);
      const updated = exists
        ? current.filter((i) => i.value !== item.value)
        : [...current, item];

      setSelected(updated);
      props.onChange?.(updated.map((i) => i.value));
    } else {
      setSelected([item]);
      setOpen(false);
      props.onChange?.(item.value);
    }
  };

  // 清除所有选中项
  const onClearAll = () => {
    if (selected().length === 0) return;
    setSelected([]);
    props.onChange?.([]); // 清除后触发 onChange 确保原资料有被触发更新
  };

  let dropdownRef: HTMLElement;
  const PopupHeaderIcon = (): JSX.Element => {
    return (
      <div class="flex flex-row items-center justify-center gap-[6px]">
        <Show when={isMultiple} fallback={<></>}>
          {/* 渐变按钮 */}
          <div class="bg-linear-180 flex h-8 items-center justify-center rounded-[8px] from-[var(--colors-surface-bg-7)] to-[var(--colors-surface-bg-6)] px-4 text-[var(--colors-text-tertiary)]">
            {t("search.clearall")}
          </div>
        </Show>
        {/* 渐变按钮 close */}
        <GradientBorderButton
          wrapClass="from-[var(--colors-surface-bg-7)] to-[var(--colors-surface-bg-6)]"
          borderClass="from-[var(--colors-border-icon-top5)] to-[var(--colors-border-icon-bottom10)]"
          onClick={() => {
            if (popupRef()) {
              popupRef()?.handleClose();
            } else {
              setOpen(false);
            }
          }}
        >
          <Icon
            name="general/cross"
            wrapperClass="size-8"
            class="size-4 text-[var(--colors-icon-btn-default)]"
          />
        </GradientBorderButton>
      </div>
    );
  };

  onMount(() => {
    if (props.isClickOutsideClose ?? true) {
      useOnClickOutside(dropdownRef, () => setOpen(false));
    }
  });

  // 过滤器
  function filterOptions(options: Option[], query: string): Option[] {
    if (!query) return options;
    const lowerQuery = query.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerQuery),
    );
  }
  // 搜索时实时过滤
  createEffect(() => {
    setFilteredOptions(filterOptions(props.options, searchQuery()));
  });

  return (
    <div
      class={cn("relative w-full max-w-xs", props.class)}
      ref={(el) => (dropdownRef = el)}
    >
      {props.label && (
        <label class="mb-1 block text-sm text-gray-600">{props.label}</label>
      )}
      <div
        class={cn(
          "flex h-full cursor-pointer items-center justify-between rounded border border-[var(--home-sk)] py-3 text-[13px] text-[var(--colors-text-primary)]",
          isMobile() ? "px-3" : "px-4",
          props.bgColor || "bg-general-enterbg",
          props.inputClass,
        )}
        onclick={toggle}
      >
        <span class={selected().length ? "" : "text-general-light"}>
          {selected().length ? (
            isMultiple ? (
              <>
                {props.showPrefix && (
                  <span class="text-general-bright font-[500]">
                    {props.prefixText || t("home.providers") + ": "}
                  </span>
                )}

                <Show when={isMobile()} fallback={<></>}>
                  <span class="text-general-bright font-[500]">
                    {t("home.providers")}:
                  </span>
                  <span class="text-general-light font-[600]">
                    +{selected().length}
                  </span>
                </Show>
                <Show when={!isMobile()} fallback={<></>}>
                  <span class="text-general-bright font-[500]">
                    {t("home.providers")}:
                  </span>
                  <span class="text-general-light font-[600]">
                    +{selected().length}
                  </span>
                </Show>
              </>
            ) : (
              <>
                {props.showSelectedPrefix && (
                  <span class="font-[500] leading-[16px] text-[var(--colors-text-secondary)]">
                    {props.selectedPrefixText || t("home.sort_by") + ": "}
                  </span>
                )}
                <span class="font-[600] leading-[16px] text-[var(--colors-text-primary)]">
                  {selected()[0].label}
                </span>
              </>
            )
          ) : (
            <>
              {props.showSelectedPrefix ? (
                <span class="text-general-bright">
                  {props.selectedPrefixText || t("home.sort_by") + ": "}
                </span>
              ) : (
                <span class="text-general-darkzi">
                  {props.placeholder || "please select"}
                </span>
              )}
            </>
          )}
        </span>

        <Show
          when={props.arrowIcon}
          fallback={
            <span
              class={`flex items-center justify-center rounded-[4px] bg-[var(--colors-btn-border-fade-10)] ${isMobile() ? "h-[22px] w-[22px]" : "h-6 w-6"}`}
            >
              <Icon
                name={"general/arrow_down"}
                class={`size-[8px] text-[var(--colors-icon-invert-btn)] transition-transform ${
                  open() ? "rotate-180" : "rotate-0"
                }`}
              />
            </span>
          }
        >
          <Icon
            name={props.arrowIcon as svgPathTypes}
            class={`text-general-icon size-[12px] transition-transform ${
              open() ? "rotate-0" : "rotate-[-90deg]"
            }`}
          />
        </Show>
      </div>

      <Show
        when={isMobile()}
        fallback={
          <div
            class={cn("absolute z-50 w-full", props.downClass)}
            style={{
              "box-shadow": "0px 3px 10px 0px rgb(4,4,4,0.3)",
              // "max-height": open() ? "377px" : "0px",
              opacity: open() ? "1" : "0",
              transform: open()
                ? "scale(1) translateY(0)"
                : "scale(0.95) translateY(-10px)",
              "transform-origin": "top center",
              transition: "max-height 0.3s ease-in-out",
              "pointer-events": open() ? "auto" : "none",
              overflow: "hidden",
            }}
          >
            <Show when={open() && isMobile()} fallback={<></>}>
              <div
                class="pointer-events-auto fixed left-0 top-0 z-0 h-screen w-screen"
                onClick={() => setOpen(false)}
              />
            </Show>

            <div class="relative z-[1] mt-[10px] flex flex-col items-center justify-start overflow-hidden rounded-[8px] bg-[var(--colors-surface-bg-8)] shadow">
              <Show when={props.showSearch} fallback={<></>}>
                <div class="w-full px-5 pb-[10px] pt-4">
                  <Input
                    id="mobile-select-search"
                    name="mobile-select-search"
                    onInput={(e) => setSearchQuery(e.currentTarget.value)}
                    value={searchQuery()}
                    class={`h-[44px] bg-[var(--colors-surface-bg-10)] ${isMobile() ? "p-[10px]" : "!gap-1 !px-16"}]`}
                    inputClass="!text-[13px]"
                    placeholder={props.searchPlaceholder}
                    IsFloatPlaceholder={false}
                    prefix={Prefix()}
                    showClose
                    onClose={() => setSearchQuery("")}
                  />
                </div>
              </Show>
              <div class="flex h-fit max-h-[300px] w-full flex-col gap-[10px] overflow-y-auto overscroll-contain px-5 pb-5 pt-4">
                {filteredOptions().map((item) => {
                  const isChecked = selected().some(
                    (s) => s.value === item.value,
                  );
                  return (
                    <li
                      class="flex h-11 cursor-pointer items-center gap-x-2.5"
                      onClick={() => selectItem(item)}
                    >
                      {isMultiple ? (
                        <>
                          <IconCheckbox checked={isChecked} />
                          <span class="flex w-full flex-1 flex-row items-center justify-between">
                            <Image
                              src={getProviderLogo(item.logo)}
                              alt={item.name}
                              class="h-7 w-[100px]"
                            />
                            <Show when={item.game_count} fallback={<></>}>
                              <span class="text-(--colors-text-primary) font-['Inter'] text-[14px] font-bold leading-[17px]">
                                {t("common.games_count", {
                                  val: item.game_count,
                                })}
                              </span>
                            </Show>
                          </span>
                        </>
                      ) : (
                        <div class="flex w-full items-center justify-between">
                          <span class="font-['Alexandria'] text-[14px] font-[700] leading-[16px] text-[var(--colors-text-primary)]">
                            {item.label}
                          </span>
                          <IconRadio
                            checked={props.defaultValue == item.value}
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
              </div>
              <Show when={isMultiple}>
                <div
                  class="border-general-sk flex h-[47px] w-full cursor-pointer items-center justify-center border-t-[1px]"
                  onClick={onClearAll}
                >
                  <span class="font-['Inter'] text-[12px] font-semibold text-[var(--colors-text-brand2)]">
                    {t("search.clearall")}
                  </span>
                </div>
              </Show>
            </div>
          </div>
        }
      >
        <Popup
          show={open()}
          onClose={() => setOpen(false)}
          title={t("common.select")}
          titleClass="text-[16px] font-bold bg-[var(--colors-surface-bg-8)]"
          contentClass={`px-0 ${props.showSearch ? "h-[70vh]" : "h-full max-h-[70dvh]l"}`}
          typeFlag={false}
          headerIcon={PopupHeaderIcon()}
          ref={(ref) => {
            setPopupRef(ref);
          }}
        >
          <div
            class={cn(
              "text-general-light flex w-full flex-col items-center justify-start bg-[var(--colors-surface-bg-8)]",
              props.downClass,
            )}
          >
            <Show when={props.showSearch} fallback={<></>}>
              <div class="mb-[10px] w-full px-4">
                <Input
                  id="mobile-select-search"
                  inputClass="placeholder:!font-bold placeholder:!text-[var(--colors-text-secondary)]"
                  name="mobile-select-search"
                  onInput={(e) => setSearchQuery(e.currentTarget.value)}
                  value={searchQuery()}
                  class="h-[40px] !gap-1 border border-[var(--colors-border-card-border2)] bg-[var(--colors-surface-bg-10)]"
                  placeholder={props.searchPlaceholder + "..."}
                  IsFloatPlaceholder={false}
                  prefix={Prefix()}
                  showClose
                  onClose={() => setSearchQuery("")}
                />
              </div>
            </Show>
            <ul class="w-full flex-1 overflow-y-auto">
              {filteredOptions().map((item) => {
                const isChecked = selected().some(
                  (s) => s.value === item.value,
                );
                return (
                  <li
                    class={`flex h-[46px] cursor-pointer items-center gap-3 px-[20px] hover:bg-[var(--colors-surface-bg-7)] ${
                      isChecked ? "bg-[var(--colors-surface-bg-7)]" : ""
                    }`}
                    onClick={() => {
                      selectItem(item);
                    }}
                  >
                    {isMultiple ? (
                      <div class="flex h-full w-full items-center gap-[10px]">
                        <IconCheckbox checked={isChecked} />
                        <span class="flex h-full w-full items-center justify-between">
                          <Image
                            src={getProviderLogo(item.logo)}
                            alt={item.label}
                            class="h-[28px]"
                            textClass="text-general-light text-[14px] font-medium"
                          />
                          <Show when={item.game_count}>
                            <span class="font-['Inter'] text-[14px] font-bold leading-[17px] text-[var(--colors-text-primary)]">
                              {t("common.games_count", {
                                val: item.game_count,
                              })}
                            </span>
                          </Show>
                        </span>
                      </div>
                    ) : (
                      <div class="flex w-full items-center justify-between">
                        <span class="font-['Alexandria'] text-[14px] font-[700] leading-[17px]">
                          {item.label}
                        </span>
                        <IconRadio checked={props.defaultValue == item.value} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </Popup>
      </Show>
    </div>
  );
}

const Image = (props: {
  src: string;
  alt: string;
  class?: string;
  textClass?: string;
}) => {
  const [imgSrc, setImgSrc] = createSignal(props.src);

  const handleError = () => {
    setImgSrc(""); // 清空 src，瀏覽器就不會顯示 broken image icon
  };

  return (
    <>
      {imgSrc() ? (
        <img
          src={imgSrc()}
          alt={props.alt}
          onError={handleError}
          class={props.class}
        />
      ) : (
        <span class={cn("text-general-light", props.textClass)}>
          {props.alt}
        </span>
      )}
    </>
  );
};
